import { useEffect, useState, useCallback, useMemo } from "react";
import { appendUnique, firstPage, listedRows, nextPage, trimPage } from "../../lib/paging";
import { LoadMore } from "./LoadMore";
import { Link, useNavigate } from "react-router";
import { Card, Button, Input, Stack, EmptyState, Cluster, GroupTree, useToast } from "@salesapp/ui";
import { crmApi, type Quote } from "../../lib/crmApi";
import { reportSaveFailure } from "../../lib/saveNotice";
import { ACTION_HINTS, FIELD_HINTS, QUOTE_STATUS_OPTIONS } from "../../help/fieldHints";
import { CompanyLookup } from "./CompanyLookup";
import { ListWorkspace, DeleteRecordButton } from "./RecordWorkspace";
import { GroupByControl } from "./GroupByControl";
import { SearchBar, StatusSlicer, useSelectOptions } from "./SearchBar";
import { StatusBadge } from "./StatusBadge";
import { useCrmHref } from "../../lib/customerUrl";
import { isAgedToHistory, quoteWorkflow } from "../../lib/localeFormat";
import { useLocale } from "../../shell/LocaleProvider";
import { useAccess } from "../../shell/AccessProvider";
import { ResponsiveDataTable, type DataColumn, type DataRow } from "./ResponsiveDataTable";

function quoteLocked(status: string | null | undefined): boolean {
  return Boolean(status) && status !== "draft" && status !== "rejected";
}

const QUOTE_STATUSES = ["draft", "pending_approval", "approved", "sent", "accepted", "rejected", "expired"];

type GroupBy = "none" | "salesperson" | "company" | "contact";

const GROUP_BY_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: "none", label: "No grouping" },
  { value: "salesperson", label: "Salesperson" },
  { value: "company", label: "Company" },
  { value: "contact", label: "Contact" },
];

function groupKeyFor(q: Quote, groupBy: GroupBy): { key: string; label: string } {
  if (groupBy === "salesperson") return { key: q.owner_user_id ?? "—", label: q.owner_name ?? "Unassigned" };
  if (groupBy === "contact") return { key: q.contact_id ?? "—", label: q.contact_name ?? "No contact" };
  return { key: q.company_id ?? "—", label: q.company_name ?? "Unknown company" };
}

const quoteColumns: DataColumn[] = [
  { key: "number", label: "Quote #", primary: true, hint: "System-generated quote reference." },
  { key: "company", label: "Company", primary: true, hint: FIELD_HINTS.opportunity_company },
  { key: "status", label: "Status", primary: true, hint: FIELD_HINTS.quote_status },
  { key: "total", label: "Total", primary: true, hint: "Quote total in the quote currency." },
  { key: "expiry", label: "Expiry", hint: FIELD_HINTS.quote_expiry_date },
];

const historyColumns: DataColumn[] = [
  ...quoteColumns.filter((c) => c.key !== "expiry"),
  { key: "order", label: "Order", primary: true, hint: "The order generated from this accepted quote." },
  { key: "accepted", label: "Accepted", hint: "When the quote was accepted." },
];

export function QuotesPage({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [notes, setNotes] = useState("");
  const [sentLink, setSentLink] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<GroupBy>("none");
  const navigate = useNavigate();
  const crmHref = useCrmHref();
  const { formatMoney, formatDate, quoteApprovalRequired, currency, historyAfterDays } = useLocale();
  const { can } = useAccess();
  const canCreateOrder = can("crm.orders", "create");
  const statuses = useSelectOptions(orgId, "quote", "quote_status", QUOTE_STATUSES);

  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const listed = listedRows(await crmApi.listQuotes(orgId, statusFilter || undefined, search || undefined, undefined, nextPage(quotes.length), { withTotal: true }));
      const page = trimPage(listed.rows);
      setQuotes((current) => appendUnique(current, page.items));
      setHasMore(page.hasMore);
      setTotal(listed.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load more quotes");
    } finally {
      setLoadingMore(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const listed = listedRows(await crmApi.listQuotes(orgId, statusFilter || undefined, search || undefined, undefined, firstPage(), { withTotal: true }));
      const page = trimPage(listed.rows);
      setQuotes(page.items);
      setHasMore(page.hasMore);
      setTotal(listed.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load quotes");
    } finally {
      setLoading(false);
    }
  }, [orgId, statusFilter, search]);

  useEffect(() => { void load(); }, [load]);

  const handleCreate = async () => {
    if (!companyId) return;
    try {
      const created = await crmApi.createQuote({ company_id: companyId, notes: notes || null, line_items: [], currency }, orgId);
      setShowForm(false);
      setCompanyName("");
      setCompanyId("");
      setNotes("");
      show("Saved!", "success");
      navigate(crmHref(`/quotes/${created.id}`));
    } catch (e) {
      setError(reportSaveFailure(show, e, "Failed to create quote"));
    }
  };

  async function run(action: () => Promise<unknown>) {
    try {
      const result = await action();
      if (result && typeof result === "object" && "accept_token" in result && typeof result.accept_token === "string") {
        setSentLink(`${window.location.origin}/quote-accept/${result.accept_token}`);
      }
      show("Saved!", "success");
      await load();
    } catch (e) {
      setError(reportSaveFailure(show, e, "Quote could not be updated"));
    }
  }

  const buildRow = (q: Quote): DataRow => {
    const locked = quoteLocked(q.quote_status);
    const flow = quoteWorkflow(
      q.quote_status,
      q.quote_approval_required ?? quoteApprovalRequired,
      Boolean(q.order_id),
      q.total ?? null,
    );
    return {
      id: q.id,
      href: crmHref(`/quotes/${q.id}`),
      cells: {
        number: <Link to={crmHref(`/quotes/${q.id}`)}>{q.quote_number}</Link>,
        company: q.company_name ?? "—",
        status: <StatusBadge status={q.quote_status} options={QUOTE_STATUS_OPTIONS} />,
        total: q.total != null ? formatMoney(q.total, q.currency) : "—",
        expiry: q.expiry_date ? formatDate(q.expiry_date) : "—",
      },
      actions: (
        <Cluster gap={2}>
          {flow.canSubmit && (
            <Button
              variant="secondary"
              size="sm"
              title="Send this quote to an approver. Required before you can send it to the client."
              onClick={() => run(() => crmApi.submitQuote(q.id, orgId))}
            >
              Submit
            </Button>
          )}
          {flow.canApprove && (
            <>
              <Button
                variant="secondary"
                size="sm"
                title="Approve this quote so it can be sent to the client."
                onClick={() => run(() => crmApi.approveQuote(q.id, undefined, orgId))}
              >
                Approve
              </Button>
              <Button
                variant="ghost"
                size="sm"
                title="Reject this quote and return it for revision."
                onClick={() => run(() => crmApi.rejectQuote(q.id, "Needs revision", orgId))}
              >
                Reject
              </Button>
            </>
          )}
          {flow.canSend && (
            <Button
              variant="secondary"
              size="sm"
              title="Send this quote to the client and create a customer accept link."
              onClick={() => run(() => crmApi.sendQuote(q.id, orgId))}
            >
              Send
            </Button>
          )}
          {flow.canAccept && (
            <Button
              variant="secondary"
              size="sm"
              title="Record that the client accepted — for example after a call — without using the online accept link."
              onClick={() => run(() => crmApi.acceptQuote(q.id, orgId))}
            >
              Mark accepted
            </Button>
          )}
          {flow.canGenerateOrder && canCreateOrder && (
            <Button
              variant="secondary"
              size="sm"
              title="Create an order from this accepted quote."
              onClick={() => run(() => crmApi.generateOrder(q.id, orgId))}
            >
              Generate order
            </Button>
          )}
          {q.quote_status === "accepted" && q.order_id && (
            <Button
              variant="secondary"
              size="sm"
              disabled
              title={`Order ${q.order_number ?? ""} has already been generated from this quote.`}
            >
              Order generated
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            title={locked ? "Open this quote (read-only in this status)." : "Open this quote to edit line items and details."}
            onClick={() => navigate(crmHref(`/quotes/${q.id}`))}
          >
            {locked ? "View" : "Edit"}
          </Button>
          <DeleteRecordButton
            capability="crm.quotes"
            recordLabel={q.quote_number ?? "quote"}
            size="sm"
            variant="ghost"
            onDeleted={async () => {
              try {
                await crmApi.deleteQuote(q.id, orgId);
                await load();
              } catch (e) {
                setError(e instanceof Error ? e.message : "Failed to delete quote");
              }
            }}
          />
        </Cluster>
      ),
    };
  };

  // An accepted quote that produced an order is finished business. It stays in the open list (greyed out)
  // for the org's "move to history after N days" setting, then drops into History.
  const isHistory = useCallback(
    (q: Quote) => q.quote_status === "accepted" && Boolean(q.order_id) && isAgedToHistory(q.order_placed_at, historyAfterDays),
    [historyAfterDays],
  );
  const openQuotes = useMemo(() => quotes.filter((q) => !isHistory(q)), [quotes, isHistory]);
  const historyQuotes = useMemo(() => quotes.filter(isHistory), [quotes, isHistory]);

  const buildHistoryRow = (q: Quote): DataRow => ({
    id: q.id,
    href: crmHref(`/quotes/${q.id}`),
    cells: {
      number: <Link to={crmHref(`/quotes/${q.id}`)}>{q.quote_number}</Link>,
      company: q.company_name ?? "—",
      status: <StatusBadge status={q.quote_status} options={QUOTE_STATUS_OPTIONS} />,
      total: q.total != null ? formatMoney(q.total, q.currency) : "—",
      order: q.order_id ? <Link to={crmHref(`/orders/${q.order_id}`)}>{q.order_number}</Link> : "—",
      accepted: q.accepted_at ? formatDate(q.accepted_at) : "—",
    },
    actions: (
      <Button variant="ghost" size="sm" title="Open this quote (read-only)." onClick={() => navigate(crmHref(`/quotes/${q.id}`))}>
        View
      </Button>
    ),
  });

  const groupedQuotes = useMemo(() => {
    if (groupBy === "none") return [];
    const byKey = new Map<string, { key: string; label: string; items: Quote[]; total: number; currency: string | null }>();
    for (const q of openQuotes) {
      const { key, label } = groupKeyFor(q, groupBy);
      let bucket = byKey.get(key);
      if (!bucket) {
        bucket = { key, label, items: [], total: 0, currency: q.currency ?? null };
        byKey.set(key, bucket);
      }
      bucket.items.push(q);
      bucket.total += q.total ?? 0;
    }
    return Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [openQuotes, groupBy]);

  return (
    <ListWorkspace
      title="Quotes"
      actions={
        <Button title={ACTION_HINTS.new_quote} onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "New quote"}
        </Button>
      }
      toolbar={
        <div className="crm-toolbar">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search quotes…"
            label="Search quotes"
            hint="Search by quote number, company, status, notes, or terms."
          />
          <StatusSlicer
            value={statusFilter}
            onChange={setStatusFilter}
            options={statuses}
            optionHints={QUOTE_STATUS_OPTIONS}
            title={ACTION_HINTS.filter_quote_status}
          />
          <GroupByControl
            label="Group quotes by"
            value={groupBy}
            options={GROUP_BY_OPTIONS}
            onChange={(next) => setGroupBy(next as GroupBy)}
          />
        </div>
      }
    >
      {error && <p className="crm-error">{error}</p>}
      {sentLink && (
        <Card>
          <p>Customer accept link</p>
          <code className="settings-setup-link">{sentLink}</code>
        </Card>
      )}
      {showForm && (
        <Card>
          <Stack gap={3}>
            <CompanyLookup
              orgId={orgId}
              value={companyName}
              companyId={companyId}
              allowCreate={false}
              onChange={({ name, companyId: nextId }) => {
                setCompanyName(name);
                setCompanyId(nextId);
              }}
            />
            <Input label="Notes" tooltip={FIELD_HINTS.quote_notes} value={notes} onChange={(e) => setNotes(e.target.value)} />
            <div className="crm-form-actions">
              <Button title={ACTION_HINTS.create_quote} onClick={() => handleCreate()} disabled={!companyId}>
                Create quote
              </Button>
            </div>
          </Stack>
        </Card>
      )}
      {loading ? (
        <Card>Loading...</Card>
      ) : quotes.length === 0 ? (
        <Card><EmptyState title="No quotes found" description={quoteApprovalRequired ? "Create a quote, submit it for approval, then send or accept it." : "Create a quote, then send it or mark it accepted."} /></Card>
      ) : groupBy === "none" ? (
        <Card className="crm-table-card">
          <ResponsiveDataTable caption="Open quotes" columns={quoteColumns} rows={openQuotes.map(buildRow)} />
        </Card>
      ) : (
        <GroupTree
          groups={groupedQuotes.map((group) => ({
            key: group.key,
            label: group.label,
            count: group.items.length,
            totalValue: formatMoney(group.total, group.currency ?? currency),
            children: (
              <ResponsiveDataTable caption={`Quotes — ${group.label}`} columns={quoteColumns} rows={group.items.map(buildRow)} />
            ),
          }))}
        />
      )}
      {!loading && historyQuotes.length > 0 && (
        <Stack gap={2}>
          <h2 className="crm-section-title">Quote history</h2>
          <p className="crm-help">Accepted quotes whose order was created more than {historyAfterDays} day(s) ago.</p>
          <Card className="crm-table-card">
            <ResponsiveDataTable caption="Quote history" columns={historyColumns} rows={historyQuotes.map(buildHistoryRow)} />
          </Card>
        </Stack>
      )}
      <LoadMore shown={quotes.length} total={total} hasMore={hasMore} loading={loadingMore} onLoadMore={() => void loadMore()} noun="quotes" />
    </ListWorkspace>
  );
}
